/**
 * App Sidebar Component (shadcn-admin style)
 * Floating sidebar with rounded corners, PAO logo, navigation, user menu, and theme switcher
 */

import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { navigationConfig, filterNavigationByRoles, NavigationItem } from '@/config/navigation'
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  User,
  LogOut,
} from 'lucide-react'

interface AppSidebarProps {
  isOpen: boolean
  onClose: () => void
  isCollapsed: boolean
  onToggleCollapse: () => void
}

function NavItem({ item, depth = 0, isCollapsed }: { item: NavigationItem; depth?: number; isCollapsed: boolean }) {
  const location = useLocation()
  const [isExpanded, setIsExpanded] = useState(false)
  const hasChildren = item.children && item.children.length > 0

  const isActive = location.pathname === item.path
  const isParentActive = hasChildren && item.children!.some((child) => location.pathname === child.path)

  const Icon = item.icon
  const paddingLeft = isCollapsed ? '0.75rem' : `${depth * 0.75 + 0.75}rem`

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`
            w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md
            transition-colors
            ${
              isParentActive
                ? 'bg-primary/10 text-primary dark:bg-primary/20'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }
            ${isCollapsed ? 'justify-center' : ''}
          `}
          style={{ paddingLeft }}
          title={isCollapsed ? item.label : undefined}
        >
          <div className="flex items-center space-x-2.5">
            <Icon className="w-4 h-4 flex-shrink-0" />
            {!isCollapsed && <span>{item.label}</span>}
          </div>
          {!isCollapsed && (
            isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )
          )}
        </button>

        {isExpanded && !isCollapsed && (
          <div className="mt-0.5 ml-2 space-y-0.5">
            {item.children!.map((child) => (
              <NavItem key={child.id} item={child} depth={depth + 1} isCollapsed={isCollapsed} />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <Link
      to={item.path}
      className={`
        flex items-center space-x-2.5 px-3 py-2 text-sm font-medium rounded-md
        transition-colors
        ${
          isActive
            ? 'bg-primary text-white shadow-sm'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
        }
        ${isCollapsed ? 'justify-center' : ''}
      `}
      style={{ paddingLeft }}
      title={isCollapsed ? item.label : undefined}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {!isCollapsed && <span>{item.label}</span>}
    </Link>
  )
}

export function AppSidebar({ isOpen, onClose, isCollapsed, onToggleCollapse }: AppSidebarProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userRoles = user?.roles || []

  const filteredNavigation = filterNavigationByRoles(navigationConfig, userRoles)

  return (
    <>
      {/* Overlay backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Floating Sidebar */}
      <aside
        className={`
          fixed z-40
          ${isCollapsed ? 'w-16' : 'w-64'}
          bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800
          shadow-xl
          transform transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          flex flex-col
          rounded-xl
          top-3 bottom-3 left-3
        `}
      >
        {/* Logo/Header Section */}
        <div className="h-14 flex items-center justify-between px-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              <img
                src="/pao-logo.svg"
                alt="PAO Logo"
                className="w-10 h-10"
              />
              <div>
                <h1 className="text-sm font-bold text-gray-900 dark:text-white">PAO</h1>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">Management</p>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="w-full flex justify-center">
              <img
                src="/pao-logo.svg"
                alt="PAO Logo"
                className="w-10 h-10"
              />
            </div>
          )}

          {/* Desktop collapse toggle */}
          <button
            onClick={onToggleCollapse}
            className="hidden lg:block p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>

          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation Section */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 custom-scrollbar">
          <div className="space-y-0.5">
            {filteredNavigation.map((item) => (
              <NavItem key={item.id} item={item} isCollapsed={isCollapsed} />
            ))}
          </div>
        </nav>

        {/* Footer Section - User Menu */}
        <div className="border-t border-gray-200 dark:border-gray-800 p-2 flex-shrink-0">
          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className={`
                w-full flex items-center gap-2 px-2 py-2 rounded-md
                hover:bg-gray-100 dark:hover:bg-gray-800
                transition-colors
                ${isCollapsed ? 'justify-center' : ''}
              `}
              title={isCollapsed ? user?.full_name : undefined}
            >
              <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-primary" />
              </div>
              {!isCollapsed && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.full_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.roles.map((r) => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}
                  </p>
                </div>
              )}
              {!isCollapsed && <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
            </button>

            {/* User Dropdown */}
            {isUserMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsUserMenuOpen(false)}
                />
                <div className={`
                  absolute ${isCollapsed ? 'left-full ml-2' : 'bottom-full mb-2'} left-0 right-0
                  bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 py-1
                  border border-gray-200 dark:border-gray-700
                  ${isCollapsed ? 'w-48' : ''}
                `}>
                  {isCollapsed && (
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {user?.full_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                    </div>
                  )}

                  <button
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    onClick={() => {
                      setIsUserMenuOpen(false)
                      navigate('/profile')
                    }}
                  >
                    <User className="w-4 h-4" />
                    <span>Profile</span>
                  </button>

                  <button
                    className="w-full px-3 py-2 text-left text-sm text-accent dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    onClick={() => {
                      setIsUserMenuOpen(false)
                      logout()
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}

export default AppSidebar
